import type {
  CustomFileMetadata,
  CustomFileData,
  FolderMetadata,
} from "../types/customFileLibrary";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api";

/**
 * Build auth headers. Sends the JWT as a Bearer token so requests work
 * cross-origin regardless of whether the browser forwards the cookie.
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem("excalidraw-auth-token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * List all files (metadata only)
 */
export async function listFiles(): Promise<CustomFileMetadata[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/files`, {
      credentials: "include",
      headers: { ...getAuthHeaders() },
    });

    if (!response.ok) {
      throw new Error(`Failed to list files: ${response.statusText}`);
    }

    const data = await response.json();
    return data.files || [];
  } catch (error) {
    console.error("Error listing files:", error);
    throw error;
  }
}

/**
 * Get a specific file (full data)
 */
export async function getFile(id: string): Promise<CustomFileData> {
  try {
    const response = await fetch(`${API_BASE_URL}/files/${id}`, {
      credentials: "include",
      headers: { ...getAuthHeaders() },
    });

    if (!response.ok) {
      throw new Error(`Failed to get file: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error getting file ${id}:`, error);
    throw error;
  }
}

/**
 * Save or update a file
 */
export async function saveFile(data: CustomFileData): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/files`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to save file: ${response.statusText}`);
    }

    await response.json();
  } catch (error) {
    console.error("Error saving file:", error);
    throw error;
  }
}

/**
 * Move a file to a folder (or remove from folder) without touching its elements.
 * Uses PATCH so the current drawing is never overwritten.
 */
export async function moveFile(
  id: string,
  folderId: string | null,
): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/files/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify({ folderId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to move file: ${response.statusText}`);
    }
  } catch (error) {
    console.error(`Error moving file ${id}:`, error);
    throw error;
  }
}

/**
 * Rename a file
 */
export async function renameFile(
  id: string,
  newName: string,
): Promise<{ name: string; lastModified: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/files/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify({ name: newName }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `Rename failed - Status: ${response.status}, Data:`,
        errorData,
      );
      throw new Error(
        `Failed to rename file: ${response.statusText} - ${
          errorData.message || errorData.error || ""
        }`,
      );
    }

    const result = await response.json();
    return {
      name: result.name,
      lastModified: result.lastModified,
    };
  } catch (error) {
    console.error(`Error renaming file ${id}:`, error);
    throw error;
  }
}

/**
 * Delete a file
 */
export async function deleteFile(id: string): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/files/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: { ...getAuthHeaders() },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete file: ${response.statusText}`);
    }

    await response.json();
  } catch (error) {
    console.error(`Error deleting file ${id}:`, error);
    throw error;
  }
}

/**
 * List all folders (metadata only)
 */
export async function listFolders(): Promise<FolderMetadata[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/folders`, {
      credentials: "include",
      headers: { ...getAuthHeaders() },
    });

    if (!response.ok) {
      throw new Error(`Failed to list folders: ${response.statusText}`);
    }

    const data = await response.json();
    return data.folders || [];
  } catch (error) {
    console.error("Error listing folders:", error);
    throw error;
  }
}

/**
 * Get a specific folder
 */
export async function getFolder(id: string): Promise<FolderMetadata> {
  try {
    const response = await fetch(`${API_BASE_URL}/folders/${id}`, {
      credentials: "include",
      headers: { ...getAuthHeaders() },
    });

    if (!response.ok) {
      throw new Error(`Failed to get folder: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error getting folder ${id}:`, error);
    throw error;
  }
}

/**
 * Create or save a folder
 */
export async function createFolder(
  data: Omit<FolderMetadata, "createdAt" | "lastModified">,
): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/folders`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to create folder: ${response.statusText}`);
    }

    await response.json();
  } catch (error) {
    console.error("Error creating folder:", error);
    throw error;
  }
}

/**
 * Update a folder
 */
export async function updateFolder(
  id: string,
  data: Partial<Omit<FolderMetadata, "id" | "createdAt">>,
): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/folders/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to update folder: ${response.statusText}`);
    }

    await response.json();
  } catch (error) {
    console.error(`Error updating folder ${id}:`, error);
    throw error;
  }
}

/**
 * Delete a folder
 */
export async function deleteFolder(id: string): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/folders/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: { ...getAuthHeaders() },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete folder: ${response.statusText}`);
    }

    await response.json();
  } catch (error) {
    console.error(`Error deleting folder ${id}:`, error);
    throw error;
  }
}
